import { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { extractProspectFromNotes } from "../../../utils/prospectExtractor";
import { ConversationModeCards } from "./ConversationModeCards";
import {
  COMPANY_SIZE_HELP,
  DEAL_STAGES,
  DEAL_STAGE_HELP,
  MEETING_TYPES,
  MEETING_TYPE_HELP,
} from "../../../constants/salesProcess";
import {
  useCreateConversation,
  useListConversations,
  useListVectorStores,
  useUploadTranscript,
  useListTranscripts,
} from "../../../api/generated/generated";
import type {
  ConversationCreate,
  ConversationResponse,
  DealStage,
  MeetingType,
  ProspectInfo,
  TranscriptInfo,
  TranscriptUploadResponse,
  UserTranscriptSummary,
} from "../../../api/generated";

interface NewConversationFormProps {
  onCreated: (conversationId: string, mode: "pre_call" | "post_call") => void;
  onCancel?: () => void;
}

export function NewConversationForm({
  onCreated,
  onCancel,
}: NewConversationFormProps) {
  const [tab, setTab] = useState(0);

  // Pre-call fields
  const [prospectName, setProspectName] = useState("");
  const [prospectCompany, setProspectCompany] = useState("");
  const [prospectTitle, setProspectTitle] = useState("");
  const [prospectLinkedin, setProspectLinkedin] = useState("");
  const [prospectNotes, setProspectNotes] = useState("");
  const [prospectIndustry, setProspectIndustry] = useState("");
  const [prospectCompanySize, setProspectCompanySize] = useState("");
  const [prospectDealStage, setProspectDealStage] = useState("");
  const [prospectMeetingType, setProspectMeetingType] = useState("");

  // Track which fields were auto-filled from notes (for helper text display)
  const autoFilled = useRef<Set<string>>(new Set());

  const handleNotesChange = (value: string) => {
    setProspectNotes(value);
    const extracted = extractProspectFromNotes(value);
    if (extracted.name && !prospectName) {
      setProspectName(extracted.name);
      autoFilled.current.add("name");
    }
    if (extracted.company && !prospectCompany) {
      setProspectCompany(extracted.company);
      autoFilled.current.add("company");
    }
    if (extracted.title && !prospectTitle) {
      setProspectTitle(extracted.title);
      autoFilled.current.add("title");
    }
  };

  // Post-call fields
  const [postCallTab, setPostCallTab] = useState(0); // 0=paste, 1=upload, 2=saved
  const [transcriptContent, setTranscriptContent] = useState("");
  const [transcriptFilename, setTranscriptFilename] = useState("");
  const [transcriptSource, setTranscriptSource] = useState<"paste" | "upload">(
    "paste",
  );
  const [selectedTranscriptId, setSelectedTranscriptId] = useState("");

  // Linking
  const [linkedPrecallId, setLinkedPrecallId] = useState("");

  // Shared
  const [vectorStoreId, setVectorStoreId] = useState("");

  const isPreCall = tab === 0;

  const vectorStores = useListVectorStores();
  const createConversation = useCreateConversation();
  const uploadTranscript = useUploadTranscript();
  const savedTranscripts = useListTranscripts({ limit: 50 });
  const precallConversations = useListConversations(
    { mode: "pre_call", limit: 20 },
    { query: { enabled: !isPreCall } },
  );
  const precallList =
    (precallConversations.data?.data as ConversationResponse[] | undefined) ??
    [];

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await uploadTranscript.mutateAsync({ data: { file } });
      const uploaded = result.data as TranscriptUploadResponse;
      setTranscriptContent(uploaded.content);
      setTranscriptFilename(uploaded.filename);
      setTranscriptSource("upload");
    } catch {
      // Error is handled by mutation state
    }
    e.target.value = "";
  };

  const handleSubmit = async () => {
    const mode = isPreCall ? "pre_call" : "post_call";

    const body: ConversationCreate = { mode };

    if (isPreCall) {
      const prospect: ProspectInfo = {};
      if (prospectName.trim()) prospect.name = prospectName.trim();
      if (prospectCompany.trim()) prospect.company = prospectCompany.trim();
      if (prospectTitle.trim()) prospect.title = prospectTitle.trim();
      if (prospectLinkedin.trim())
        prospect.linkedin_url = prospectLinkedin.trim();
      if (prospectNotes.trim()) prospect.notes = prospectNotes.trim();
      if (prospectIndustry.trim()) prospect.industry = prospectIndustry.trim();
      if (prospectCompanySize) prospect.company_size = prospectCompanySize;
      if (prospectDealStage) prospect.deal_stage = prospectDealStage as DealStage;
      if (prospectMeetingType)
        prospect.meeting_type = prospectMeetingType as MeetingType;
      body.prospect = prospect;
    } else if (postCallTab === 2 && selectedTranscriptId) {
      body.transcript_id = selectedTranscriptId;
    } else {
      const transcript: TranscriptInfo = {
        content: transcriptContent,
        source: transcriptSource,
      };
      if (transcriptFilename) transcript.filename = transcriptFilename;
      body.transcript = transcript;
    }

    if (vectorStoreId) {
      body.vector_store_id = vectorStoreId;
    }
    if (linkedPrecallId) {
      body.linked_precall_id = linkedPrecallId;
    }

    try {
      const conv = await createConversation.mutateAsync({ data: body });
      const created = conv.data as ConversationResponse;
      onCreated(created.id, mode);
    } catch {
      // Error is handled by mutation state
    }
  };

  const canSubmit = isPreCall
    ? !!(prospectName.trim() || prospectCompany.trim() || prospectNotes.trim())
    : postCallTab === 2
      ? !!selectedTranscriptId
      : !!transcriptContent.trim();

  const isSubmitting = createConversation.isPending;

  const savedData =
    savedTranscripts.data?.status === 200
      ? savedTranscripts.data.data
      : undefined;
  const transcriptList = savedData?.transcripts ?? [];

  return (
    <Box>
      <ConversationModeCards
        value={isPreCall ? "pre_call" : "post_call"}
        onChange={(mode) => setTab(mode === "pre_call" ? 0 : 1)}
      />
      {isPreCall ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Freeform Notes (CRM data, ZoomInfo, etc.)"
            multiline
            rows={3}
            value={prospectNotes}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="Paste CRM or prospect data here — name and company will be auto-detected"
            slotProps={{
              htmlInput: { maxLength: 5000 }
            }}
          />
          <TextField
            label="Prospect Name"
            value={prospectName}
            onChange={(e) => {
              setProspectName(e.target.value);
              autoFilled.current.delete("name");
            }}
            helperText={
              autoFilled.current.has("name")
                ? "Auto-filled from notes"
                : undefined
            }
            slotProps={{
              htmlInput: { maxLength: 200 }
            }}
          />
          <TextField
            label="Company"
            value={prospectCompany}
            onChange={(e) => {
              setProspectCompany(e.target.value);
              autoFilled.current.delete("company");
            }}
            helperText={
              autoFilled.current.has("company")
                ? "Auto-filled from notes"
                : undefined
            }
            slotProps={{
              htmlInput: { maxLength: 200 }
            }}
          />
          <TextField
            label="Job Title"
            value={prospectTitle}
            onChange={(e) => {
              setProspectTitle(e.target.value);
              autoFilled.current.delete("title");
            }}
            helperText={
              autoFilled.current.has("title")
                ? "Auto-filled from notes"
                : undefined
            }
            slotProps={{
              htmlInput: { maxLength: 200 }
            }}
          />
          <TextField
            label="LinkedIn URL"
            value={prospectLinkedin}
            onChange={(e) => setProspectLinkedin(e.target.value)}
            slotProps={{
              htmlInput: { maxLength: 500 }
            }}
          />
          <TextField
            label="Industry"
            value={prospectIndustry}
            onChange={(e) => setProspectIndustry(e.target.value)}
            slotProps={{
              htmlInput: { maxLength: 200 }
            }}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Company Size</InputLabel>
              <Select
                value={prospectCompanySize}
                onChange={(e) => setProspectCompanySize(e.target.value)}
                label="Company Size"
              >
                <MenuItem value="">—</MenuItem>
                <MenuItem value="1-10">1-10</MenuItem>
                <MenuItem value="11-50">11-50</MenuItem>
                <MenuItem value="51-200">51-200</MenuItem>
                <MenuItem value="201-1000">201-1000</MenuItem>
                <MenuItem value="1000+">1000+</MenuItem>
              </Select>
              <FormHelperText>{COMPANY_SIZE_HELP}</FormHelperText>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Deal Stage</InputLabel>
              <Select
                value={prospectDealStage}
                onChange={(e) => setProspectDealStage(e.target.value)}
                label="Deal Stage"
              >
                <MenuItem value="">—</MenuItem>
                {DEAL_STAGES.map((stage) => (
                  <MenuItem key={stage} value={stage}>
                    {stage}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{DEAL_STAGE_HELP}</FormHelperText>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Meeting Type</InputLabel>
              <Select
                value={prospectMeetingType}
                onChange={(e) => setProspectMeetingType(e.target.value)}
                label="Meeting Type"
              >
                <MenuItem value="">—</MenuItem>
                {MEETING_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{MEETING_TYPE_HELP}</FormHelperText>
            </FormControl>
          </Box>
        </Box>
      ) : (
        <Box>
          <Tabs
            value={postCallTab}
            onChange={(_, v) => setPostCallTab(v)}
            variant="fullWidth"
            sx={{
              mb: 1,
              borderBottom: 1,
              borderColor: "divider",
              "& .Mui-selected": { fontWeight: 700 },
            }}
          >
            <Tab label="Paste" />
            <Tab label="Upload" />
            <Tab label="Saved" icon={<BookmarkIcon />} iconPosition="start" />
          </Tabs>

          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              mb: 2
            }}>
            {postCallTab === 0
              ? "Paste the transcript text below."
              : postCallTab === 1
                ? "Upload a file (PDF, DOCX, TXT, MD, or CSV)."
                : "Choose one of your saved transcripts."}
          </Typography>

          {postCallTab === 0 && (
            <TextField
              label="Transcript"
              multiline
              rows={8}
              required
              fullWidth
              value={transcriptContent}
              onChange={(e) => {
                setTranscriptContent(e.target.value);
                setTranscriptSource("paste");
                setTranscriptFilename("");
              }}
              placeholder="Paste your call transcript here..."
              slotProps={{
                htmlInput: { maxLength: 200000 }
              }}
            />
          )}

          {postCallTab === 1 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={
                    uploadTranscript.isPending ? (
                      <CircularProgress size={16} />
                    ) : (
                      <UploadFileIcon />
                    )
                  }
                  disabled={uploadTranscript.isPending}
                >
                  Upload File
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.docx,.txt,.md,.csv"
                    onChange={handleFileUpload}
                  />
                </Button>
                {transcriptFilename && (
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    {transcriptFilename}
                  </Typography>
                )}
              </Box>
              {uploadTranscript.isError && (
                <Typography variant="body2" color="error">
                  Failed to extract text from file. Please try a different file
                  or paste directly.
                </Typography>
              )}
              {transcriptContent && transcriptSource === "upload" && (
                <TextField
                  label="Extracted Content"
                  multiline
                  rows={6}
                  fullWidth
                  value={transcriptContent}
                  slotProps={{
                    input: { readOnly: true }
                  }}
                />
              )}
            </Box>
          )}

          {postCallTab === 2 && (
            <Box>
              {savedTranscripts.isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : transcriptList.length === 0 ? (
                <Typography
                  sx={{
                    color: "text.secondary",
                    py: 4,
                    textAlign: "center"
                  }}>
                  No saved transcripts. Create one from the Transcripts page.
                </Typography>
              ) : (
                <List sx={{ maxHeight: 300, overflow: "auto" }}>
                  {transcriptList.map((t: UserTranscriptSummary) => (
                    <ListItemButton
                      key={t.id}
                      selected={selectedTranscriptId === t.id}
                      onClick={() => setSelectedTranscriptId(t.id)}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2">{t.title}</Typography>
                            <Chip
                              label={t.source}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            {new Date(t.date).toLocaleDateString()}
                            {t.preview &&
                              ` \u2014 ${t.preview.slice(0, 60)}...`}
                          </>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          )}
        </Box>
      )}
      {/* Linked pre-call selector (post-call only) */}
      {!isPreCall && precallList.length > 0 && (
        <FormControl fullWidth sx={{ mt: 3 }}>
          <InputLabel>Link Pre-Call Research (optional)</InputLabel>
          <Select
            value={linkedPrecallId}
            onChange={(e) => setLinkedPrecallId(e.target.value)}
            label="Link Pre-Call Research (optional)"
          >
            <MenuItem value="">None</MenuItem>
            {precallList.map((conv) => {
              const p = conv.prospect;
              const detail = [p?.title, p?.company].filter(Boolean).join(", ");
              return (
                <MenuItem key={conv.id} value={conv.id}>
                  <Box>
                    <Typography variant="body2">{conv.title}</Typography>
                    {detail && (
                      <Typography variant="caption" sx={{
                        color: "text.secondary"
                      }}>
                        {detail}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      )}
      {/* Vector store selector */}
      {vectorStores.data?.data && vectorStores.data.data.length > 0 && (
        <FormControl fullWidth sx={{ mt: 3 }}>
          <InputLabel>Knowledge Base (optional)</InputLabel>
          <Select
            value={vectorStoreId}
            onChange={(e) => setVectorStoreId(e.target.value)}
            label="Knowledge Base (optional)"
          >
            <MenuItem value="">None</MenuItem>
            {vectorStores.data.data.map((vs) => (
              <MenuItem key={vs.id} value={vs.id}>
                {vs.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {createConversation.isError && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {isPreCall
            ? "Couldn't start the research. Please try again."
            : "Couldn't start the analysis. Please try again."}
        </Typography>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          {isPreCall ? "Start Research" : "Analyze Call"}
        </Button>
      </Box>
    </Box>
  );
}
